// src/pages/CRM/SFA/OpportunityDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, IconButton, Chip, Avatar,
  Tab, Tabs, Divider, CircularProgress, Alert, Stack, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, InputAdornment,
  Stepper, Step, StepLabel, Container, useTheme, Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  Description as QuoteIcon,
  People as TeamIcon,
  ShoppingCart as ProductIcon,
  CheckCircle as WonIcon,
  Cancel as LostIcon,
  PieChart as ProbabilityIcon,
  CalendarToday as CalendarIcon,
  Contacts as ContactsIcon,
  Star as StarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { opportunityService, productService, opportunityContactService } from './sfaService';
import axiosInstance from '../../../../utils/axiosInstance';
import {
  OpportunityStageLabels, OpportunityStageColors,
  OpportunityTypeLabels, ForecastCategoryLabels,
  formatCurrency, formatPercent, TeamMemberRoles,
  AllStages, ContactRoles
} from './sfaEnums';

// --- Custom Tab Panel ---
const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ py: 3, animation: 'fadeIn 0.3s ease-in' }}>
    {value === index && children}
  </Box>
);

// --- Stat Card Component ---
const StatCard = ({ icon, label, value, color, subValue }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
      }
    }}
  >
    <Avatar variant="rounded" sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, my: 0.5 }}>
        {value}
      </Typography>
      {subValue && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {subValue}
        </Typography>
      )}
    </Box>
  </Paper>
);

// --- Info Row Component ---
const InfoRow = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={500}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500} color="text.primary">
      {value || '-'}
    </Typography>
  </Box>
);

const OpportunityDetail = () => {
  const { chatbotId, opportunityId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [opportunity, setOpportunity] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Dialogs
  const [teamDialog, setTeamDialog] = useState({ open: false, member: null });
  const [productDialog, setProductDialog] = useState({ open: false, item: null });
  const [contactDialog, setContactDialog] = useState({ open: false, contact: null });

  // Products, Users, and Contacts for dialogs
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [opportunityContacts, setOpportunityContacts] = useState([]);

  // Permissions
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'Opportunity Details';
    checkPermissions();
    loadOpportunity();
    loadProducts();
    loadUsers();
    loadAvailableContacts();
    loadOpportunityContacts();
  }, [opportunityId]);

  const checkPermissions = async () => {
    try {
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadOpportunity = async () => {
    setLoading(true);
    try {
      const response = await opportunityService.getById(opportunityId);
      if (response.success) {
        setOpportunity(response.data);
        document.title = `${response.data.name} | Opportunity`;
      }
    } catch (err) {
      enqueueSnackbar('Failed to load opportunity', { variant: 'error' });
      navigate(`/chatbot/${chatbotId}/crm/opportunities`);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getAll({ per_page: 100, status: 'ACTIVE' });
      if (response.success) setProducts(response.data);
    } catch (err) { console.error(err); }
  };

  const loadUsers = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/teams/available-managers');
      if (response.data.success) {
        const fetchedUsers = response.data.data || [];
        setUsers(Array.isArray(fetchedUsers) ? fetchedUsers : []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    }
  };

  const loadAvailableContacts = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/contacts', { params: { per_page: 100 } });
      if (response.data.success) {
        setAvailableContacts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading contacts:', err);
      setAvailableContacts([]);
    }
  };

  const loadOpportunityContacts = async () => {
    try {
      const response = await opportunityContactService.getAll(opportunityId);
      if (response.success) {
        setOpportunityContacts(response.data || []);
      }
    } catch (err) {
      console.error('Error loading opportunity contacts:', err);
      setOpportunityContacts([]);
    }
  };

  // --- Handlers (Add/Remove Team/Product) ---
  const handleAddTeamMember = async (data) => {
    try {
      await opportunityService.addTeamMember(opportunityId, data);
      enqueueSnackbar('Team member added', { variant: 'success' });
      setTeamDialog({ open: false, member: null });
      loadOpportunity();
    } catch (err) { enqueueSnackbar('Failed to add team member', { variant: 'error' }); }
  };

  const handleRemoveTeamMember = async (memberId) => {
    try {
      await opportunityService.removeTeamMember(opportunityId, memberId);
      enqueueSnackbar('Team member removed', { variant: 'success' });
      loadOpportunity();
    } catch (err) { enqueueSnackbar('Failed to remove team member', { variant: 'error' }); }
  };

  const handleAddProduct = async (data) => {
    try {
      await opportunityService.addProduct(opportunityId, data);
      enqueueSnackbar('Product added', { variant: 'success' });
      setProductDialog({ open: false, item: null });
      loadOpportunity();
    } catch (err) { enqueueSnackbar('Failed to add product', { variant: 'error' }); }
  };

  const handleRemoveProduct = async (itemId) => {
    try {
      await opportunityService.removeProduct(opportunityId, itemId);
      enqueueSnackbar('Product removed', { variant: 'success' });
      loadOpportunity();
    } catch (err) { enqueueSnackbar('Failed to remove product', { variant: 'error' }); }
  };

  // --- Contact Handlers ---
  const handleAddContact = async (data) => {
    try {
      await opportunityContactService.add(opportunityId, data);
      enqueueSnackbar('Contact added to opportunity', { variant: 'success' });
      setContactDialog({ open: false, contact: null });
      loadOpportunityContacts();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to add contact', { variant: 'error' });
    }
  };

  const handleUpdateContact = async (contactLinkId, data) => {
    try {
      await opportunityContactService.update(opportunityId, contactLinkId, data);
      enqueueSnackbar('Contact updated', { variant: 'success' });
      loadOpportunityContacts();
    } catch (err) {
      enqueueSnackbar('Failed to update contact', { variant: 'error' });
    }
  };

  const handleRemoveContact = async (contactLinkId) => {
    try {
      await opportunityContactService.remove(opportunityId, contactLinkId);
      enqueueSnackbar('Contact removed from opportunity', { variant: 'success' });
      loadOpportunityContacts();
    } catch (err) {
      enqueueSnackbar('Failed to remove contact', { variant: 'error' });
    }
  };

  const handleSetPrimaryContact = async (contactLinkId) => {
    try {
      await opportunityContactService.update(opportunityId, contactLinkId, { is_primary: true });
      enqueueSnackbar('Primary contact updated', { variant: 'success' });
      loadOpportunityContacts();
    } catch (err) {
      enqueueSnackbar('Failed to set primary contact', { variant: 'error' });
    }
  };

  // --- Render Helpers ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!opportunity) return <Alert severity="error">Opportunity not found.</Alert>;

  const isClosedWon = opportunity.stage === 'CLOSED_WON';
  const isClosedLost = opportunity.stage === 'CLOSED_LOST';
  const isClosed = isClosedWon || isClosedLost;
  
  // Calculate active step for Stepper
  const activeStep = AllStages.indexOf(opportunity.stage);

  return (
    <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', pb: 5, mt: { xs: 2, sm: 10 } }}>
      {/* --- Top Header Area --- */}
      <Paper sx={{ px: { xs: 1.5, sm: 3 }, py: 3, mb: 4, borderRadius: 0, borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
        <Container maxWidth="xl">
          {/* Breadcrumb / Back */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities`)}
              sx={{ color: 'text.secondary', mr: 2, textTransform: 'none', fontWeight: 500 }}
            >
              Back to Opportunities
            </Button>
          </Box>

          {/* Title Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {opportunity.name}
                </Typography>
                <Chip
                  label={opportunity.opportunity_number || 'No ID'}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1, fontWeight: 600, bgcolor: 'grey.50', borderColor: 'grey.300' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="body1" color="text.secondary" fontWeight={500}>
                  {opportunity.account?.name || 'No Account Linked'}
                </Typography>
              </Box>
            </Box>

            <Box>
              {canModify && !isClosed && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/${opportunityId}/edit`)}
                  sx={{ borderRadius: 2, px: 3, py: 1, boxShadow: theme.shadows[2], textTransform: 'none', fontWeight: 600 }}
                >
                  Edit Opportunity
                </Button>
              )}
            </Box>
          </Box>

          {/* Pipeline Stepper */}
          <Box sx={{ mt: 5, mb: 1, overflowX: 'auto' }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {AllStages.map((stage) => {
                const label = OpportunityStageLabels[stage];
                const displayLabel = label.replace('Closed ', ''); 
                return (
                  <Step key={stage}>
                    <StepLabel>{displayLabel}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* --- Closed Status Alert --- */}
        {isClosed && (
          <Alert
            severity={isClosedWon ? 'success' : 'error'}
            variant="filled"
            icon={isClosedWon ? <WonIcon fontSize="inherit" /> : <LostIcon fontSize="inherit" />}
            sx={{ mb: 4, borderRadius: 2, fontWeight: 500, boxShadow: theme.shadows[2] }}
          >
            <Typography variant="subtitle1" fontWeight={700} component="span">
              {isClosedWon ? 'Deal Won!' : 'Deal Lost'}
            </Typography>
            {' — '}
            {isClosedWon 
              ? `Congratulations! ${opportunity.win_reason || 'Deal closed successfully.'}`
              : `Reason: ${opportunity.loss_reason || 'No reason provided.'}`}
          </Alert>
        )}

        {/* --- Key Metrics Grid --- */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<MoneyIcon />}
              color="#2e7d32"
              label="Deal Amount"
              value={formatCurrency(opportunity.amount, opportunity.currency)}
              subValue={opportunity.amount ? `Weighted: ${formatCurrency(opportunity.weighted_amount, opportunity.currency)}` : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<ProbabilityIcon />}
              color={opportunity.probability > 50 ? '#ed6c02' : '#9e9e9e'}
              label="Probability"
              value={formatPercent(opportunity.probability)}
              subValue={`Stage: ${OpportunityStageLabels[opportunity.stage]}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<CalendarIcon />}
              color="#1976d2"
              label="Expected Close"
              value={opportunity.close_date ? new Date(opportunity.close_date).toLocaleDateString() : '-'}
              subValue={opportunity.close_date ? `${Math.ceil((new Date(opportunity.close_date) - new Date()) / (1000 * 60 * 60 * 24))} days left` : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<PersonIcon />}
              color="#9c27b0"
              label="Opportunity Owner"
              value={`User #${opportunity.owner_user_id || '?'}`}
              subValue="Primary Owner"
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* --- LEFT COLUMN: Tabs & Main Content --- */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: theme.shadows[1] }}>
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  bgcolor: 'grey.50',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': { fontWeight: 600, minHeight: 64, textTransform: 'none', fontSize: '0.95rem' },
                  '& .Mui-selected': { color: 'primary.main', bgcolor: 'white' }
                }}
              >
                <Tab icon={<TimelineIcon />} iconPosition="start" label="Details" />
                <Tab icon={<ProductIcon />} iconPosition="start" label={`Products (${opportunity.line_items?.length || 0})`} />
                <Tab icon={<TeamIcon />} iconPosition="start" label="Team" />
                <Tab icon={<QuoteIcon />} iconPosition="start" label="Quotes" />
                <Tab icon={<ContactsIcon />} iconPosition="start" label={`Contacts (${opportunityContacts.length})`} />
              </Tabs>

              {/* DETAILS TAB */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ px: { xs: 2, sm: 4 } }}>
                  <Typography variant="h6" gutterBottom fontWeight={700} sx={{ mb: 3 }}>General Information</Typography>
                  <Grid container spacing={4} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                      <InfoRow label="Opportunity Type" value={OpportunityTypeLabels[opportunity.opportunity_type]} />
                      <InfoRow label="Lead Source" value={opportunity.lead_source} />
                      <InfoRow label="Campaign" value={opportunity.campaign_id} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoRow label="Forecast Category" value={ForecastCategoryLabels[opportunity.forecast_category]} />
                      <InfoRow label="Next Step" value={opportunity.next_step} />
                      <InfoRow label="Primary Competitor" value={opportunity.primary_competitor} />
                    </Grid>
                    {opportunity.description && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={500}>Description</Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{opportunity.description}</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 4 }} />
                  
                  <Typography variant="h6" gutterBottom fontWeight={700}>Stage History</Typography>
                  {opportunity.stage_history && opportunity.stage_history.length > 0 ? (
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      {opportunity.stage_history.map((entry, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main', mt: 2 }} />
                            {idx !== opportunity.stage_history.length - 1 && <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', my: 0.5 }} />}
                          </Box>
                          <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2 }}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                Moved to <span style={{ color: OpportunityStageColors[entry.to_stage] }}>{OpportunityStageLabels[entry.to_stage]}</span>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Updated by User #{entry.user_id}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                              {new Date(entry.date).toLocaleString()}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" variant="body2">No history available.</Typography>
                  )}
                </Box>
              </TabPanel>

              {/* PRODUCTS TAB */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ px: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    {canModify && !isClosed && (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setProductDialog({ open: true, item: null })}>
                        Add Product
                      </Button>
                    )}
                  </Box>
                  
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 700 }}>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Disc.</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          {canModify && !isClosed && <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {opportunity.line_items?.length > 0 ? (
                          opportunity.line_items.map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.product?.sku}</Typography>
                              </TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                {item.discount_amount > 0 ? `-${formatCurrency(item.discount_amount)}` : '-'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.total_price)}</TableCell>
                              {canModify && !isClosed && (
                                <TableCell align="center">
                                  <IconButton size="small" color="error" onClick={() => handleRemoveProduct(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                              <ProductIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                              <Typography color="text.secondary">No products added yet.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {opportunity.line_items?.length > 0 && (
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell colSpan={4} align="right">
                              <Typography variant="subtitle2">Grand Total</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" color="primary">{formatCurrency(opportunity.amount)}</Typography>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </TabPanel>

              {/* TEAM TAB */}
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ px: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    {canModify && !isClosed && (
                      <Button 
                        variant="outlined" 
                        startIcon={<AddIcon />} 
                        onClick={() => setTeamDialog({ open: true, member: null })}
                      >
                        Add Member
                      </Button>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    {opportunity.team_members?.map((member) => (
                      <Grid item xs={12} sm={6} key={member.id}>
                        <Card variant="outlined" sx={{ borderRadius: 2, transition: '0.2s', '&:hover': { boxShadow: 2 } }}>
                          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: '16px !important' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                              {member.user_name?.charAt(0).toUpperCase() || 'U'}
                            </Avatar>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                {member.user_name || `User #${member.user_id}`}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ fontSize: '0.8rem' }}
                                noWrap
                              >
                                {member.user_email || 'No email'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip 
                                  label={member.role} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                                {member.is_primary && (
                                  <Chip 
                                    label="Primary" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }} 
                                  />
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography variant="h6" color="primary" fontWeight={700}>
                                {member.split_percentage}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Split
                              </Typography>
                              {!member.is_primary && canModify && !isClosed && (
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleRemoveTeamMember(member.id)} 
                                  sx={{ mt: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                    {(!opportunity.team_members || opportunity.team_members.length === 0) && (
                      <Grid item xs={12}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 4, 
                          bgcolor: 'grey.50', 
                          borderRadius: 2, 
                          border: '1px dashed', 
                          borderColor: 'divider' 
                        }}>
                          <TeamIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">No team members assigned.</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </TabPanel>

              {/* QUOTES TAB */}
              <TabPanel value={tabValue} index={3}>
                <Box sx={{ px: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    {canModify && !isClosed && (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/new?opportunity_id=${opportunityId}`)}>
                        Create Quote
                      </Button>
                    )}
                  </Box>
                  <Stack spacing={2}>
                    {opportunity.quotes?.length > 0 ? (
                      opportunity.quotes.map((quote) => (
                        <Card 
                          key={quote.id} 
                          variant="outlined" 
                          sx={{ 
                            cursor: 'pointer', 
                            transition: '0.2s', 
                            borderRadius: 2,
                            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover', transform: 'translateX(4px)' } 
                          }}
                          onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/${quote.id}`)}
                        >
                          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar variant="rounded" sx={{ bgcolor: 'grey.100', color: 'grey.600' }}>
                                <QuoteIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>{quote.name}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">{quote.quote_number}</Typography>
                                  <Chip label={quote.status} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                </Stack>
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" fontWeight={700}>{formatCurrency(quote.total_amount)}</Typography>
                              <Typography variant="caption" color="text.secondary">Total</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <QuoteIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">No quotes created yet.</Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </TabPanel>

              {/* CONTACTS TAB */}
              <TabPanel value={tabValue} index={4}>
                <Box sx={{ px: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight={600}>Contact Roles</Typography>
                    {canModify && !isClosed && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setContactDialog({ open: true, contact: null })}
                      >
                        Add Contact
                      </Button>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {opportunityContacts.length > 0 ? (
                      opportunityContacts.map((link) => (
                        <Grid item xs={12} md={6} key={link.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              transition: '0.2s',
                              borderColor: link.is_primary ? 'primary.main' : 'divider',
                              bgcolor: link.is_primary ? 'primary.50' : 'white',
                              '&:hover': { boxShadow: 2 }
                            }}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: link.is_primary ? 'primary.main' : 'grey.400' }}>
                                    {link.contact?.full_name?.[0] || <PersonIcon />}
                                  </Avatar>
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle1" fontWeight={600}>
                                        {link.contact?.full_name}
                                      </Typography>
                                      {link.is_primary && (
                                        <Chip
                                          icon={<StarIcon sx={{ fontSize: 14 }} />}
                                          label="Primary"
                                          size="small"
                                          color="primary"
                                          sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                    <Chip
                                      label={link.role}
                                      size="small"
                                      variant="outlined"
                                      sx={{ mt: 0.5, height: 22, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                </Box>
                                {canModify && !isClosed && (
                                  <Box>
                                    {!link.is_primary && (
                                      <Tooltip title="Set as Primary">
                                        <IconButton size="small" onClick={() => handleSetPrimaryContact(link.id)}>
                                          <StarIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <IconButton size="small" color="error" onClick={() => handleRemoveContact(link.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>

                              <Divider sx={{ my: 1.5 }} />

                              <Stack spacing={1}>
                                {link.contact?.email && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmailIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {link.contact.email}
                                    </Typography>
                                  </Box>
                                )}
                                {link.contact?.phone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PhoneIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {link.contact.phone}
                                    </Typography>
                                  </Box>
                                )}
                                {link.contact?.title && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {link.contact.title} {link.contact.company && `at ${link.contact.company}`}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>

                              {link.notes && (
                                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {link.notes}
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Box sx={{
                          textAlign: 'center',
                          py: 6,
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: 'divider'
                        }}>
                          <ContactsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">No contacts linked to this opportunity.</Typography>
                          {canModify && !isClosed && (
                            <Button
                              sx={{ mt: 2 }}
                              startIcon={<AddIcon />}
                              onClick={() => setContactDialog({ open: true, contact: null })}
                            >
                              Add First Contact
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </TabPanel>
            </Paper>
          </Grid>

          {/* --- RIGHT COLUMN: Sidebar --- */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* Account Widget */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>ACCOUNT</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {opportunity.account ? (
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', p: 1.5, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients/${opportunity.account.id}`)}
                    >
                      <Avatar variant="rounded" sx={{ bgcolor: 'primary.main' }}><BusinessIcon /></Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{opportunity.account.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{opportunity.account.industry || 'No Industry'}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No account linked</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Contact Widget */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>PRIMARY CONTACT</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {opportunity.contact ? (
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', p: 1.5, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${opportunity.contact.id}`)}
                    >
                      <Avatar src={opportunity.contact.profile_image_url} sx={{ bgcolor: 'secondary.main' }}>
                        {opportunity.contact.first_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{opportunity.contact.full_name}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {opportunity.contact.email}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No primary contact</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Tags Widget */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>TAGS</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {opportunity.tags && opportunity.tags.length > 0 ? (
                      opportunity.tags.map((tag, idx) => (
                        <Chip key={idx} label={tag} size="small" sx={{ borderRadius: 1 }} />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No tags assigned</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* System Info Widget */}
              <Card variant="outlined" sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>SYSTEM INFO</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="caption" fontWeight={500}>{new Date(opportunity.created_at).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                      <Typography variant="caption" fontWeight={500}>{new Date(opportunity.updated_at).toLocaleString()}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* --- Dialogs --- */}
      <TeamMemberDialog
        open={teamDialog.open}
        onClose={() => setTeamDialog({ open: false, member: null })}
        onSave={handleAddTeamMember}
        users={users}
      />

      <ProductDialog
        open={productDialog.open}
        onClose={() => setProductDialog({ open: false, item: null })}
        onSave={handleAddProduct}
        products={products}
      />

      <ContactDialog
        open={contactDialog.open}
        onClose={() => setContactDialog({ open: false, contact: null })}
        onSave={handleAddContact}
        contacts={availableContacts.filter(c => !opportunityContacts.some(oc => oc.contact?.id === c.id))}
      />
    </Box>
  );
};

// --- Dialog Components ---

const TeamMemberDialog = ({ open, onClose, onSave, users = [] }) => {
  const [form, setForm] = useState({
    user_id: '',
    role: 'Sales Rep',
    split_percentage: 0
  });

  const handleSave = () => {
    if (!form.user_id) return;
    onSave(form);
    setForm({ user_id: '', role: 'Sales Rep', split_percentage: 0 });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Add Team Member</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Autocomplete
            fullWidth
            options={users}
            getOptionLabel={(option) => option.name || option.email || 'Unknown'}
            value={users.find(u => u.user_id === form.user_id) || null}
            onChange={(e, user) => setForm({ ...form, user_id: user?.user_id || '' })}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                  {option.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="User" required />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={form.role}
              label="Role"
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {TeamMemberRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Commission Split"
            type="number"
            value={form.split_percentage}
            onChange={(e) => setForm({ ...form, split_percentage: parseFloat(e.target.value) || 0 })}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!form.user_id}>
          Add Member
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProductDialog = ({ open, onClose, onSave, products }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({
    quantity: 1,
    unit_price: 0,
    discount_value: 0
  });

  useEffect(() => {
    if (selectedProduct) {
      setForm(prev => ({ ...prev, unit_price: selectedProduct.standard_price || 0 }));
    }
  }, [selectedProduct]);

  const handleSave = () => {
    if (!selectedProduct) return;
    onSave({
      product_id: selectedProduct.id,
      name: selectedProduct.name,
      ...form
    });
    setSelectedProduct(null);
    setForm({ quantity: 1, unit_price: 0, discount_value: 0 });
  };

  const total = (form.quantity * form.unit_price) - form.discount_value;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Add Product</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Autocomplete
            options={products}
            getOptionLabel={(option) => `${option.name} (${option.sku || 'No SKU'})`}
            value={selectedProduct}
            onChange={(e, newValue) => setSelectedProduct(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select Product" required />
            )}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Discount"
            type="number"
            value={form.discount_value}
            onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />
          {selectedProduct && (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 1 }}>
              <Typography variant="body2">
                Line Total: <strong>{formatCurrency(total)}</strong>
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!selectedProduct}>
          Add Product
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContactDialog = ({ open, onClose, onSave, contacts = [] }) => {
  const [form, setForm] = useState({
    contact_id: '',
    role: 'Other',
    is_primary: false,
    notes: ''
  });

  const handleSave = () => {
    if (!form.contact_id) return;
    onSave(form);
    setForm({ contact_id: '', role: 'Other', is_primary: false, notes: '' });
  };

  const handleClose = () => {
    setForm({ contact_id: '', role: 'Other', is_primary: false, notes: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Add Contact to Opportunity</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Autocomplete
            fullWidth
            options={contacts}
            getOptionLabel={(option) => option.full_name || `${option.first_name} ${option.last_name}` || option.email || 'Unknown'}
            value={contacts.find(c => c.id === form.contact_id) || null}
            onChange={(e, contact) => setForm({ ...form, contact_id: contact?.id || '' })}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'secondary.main', fontSize: '0.875rem' }}>
                  {option.first_name?.charAt(0).toUpperCase() || 'C'}
                </Avatar>
                <Box>
                  <Typography variant="body2">{option.full_name || `${option.first_name} ${option.last_name}`}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email} {option.title && `• ${option.title}`}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Contact" required />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Role on This Deal</InputLabel>
            <Select
              value={form.role}
              label="Role on This Deal"
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {ContactRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g., Key decision maker, prefers email communication"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!form.contact_id}>
          Add Contact
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpportunityDetail;